import PageSkeleton from "@/components/page-skeleton";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import {
  CompanyDTO,
  UserHistoryDTO,
  UserHistoryTargetSearchCompanies,
} from "@shaarp/common";
import { Loader2Icon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FaChevronRight, FaHistory } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import AvatarCompany from "../company/avatar-company";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useToastError } from "../ui/use-toast";

type HistoryItem = {
  type: "search" | "company";
  id: string;
  title: string;
  lastVisited: Date;
  company?: CompanyDTO;
  searchQuery?: string;
  searchId?: string;
  aggregatedCount?: number;
};

export const RecentHistoryList = ({
  className = "",
}: {
  className?: string;
}) => {
  const api = useApi();
  const { t, currentLanguage } = useTranslation();
  const toastError = useToastError();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [canLoadMore, setCanLoadMore] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [companiesData, searchesData] = await Promise.all([
          fetchCompanies(currentLimit),
          fetchUserHistorySearches(currentLimit),
        ]);

        const mergedHistory = mergeAndSortHistory(
          companiesData,
          searchesData,
          currentLimit
        );
        setHistoryItems(mergedHistory);

        if (
          companiesData.length < currentLimit &&
          searchesData.length < currentLimit
        ) {
          setCanLoadMore(false);
        }
      } catch (error) {
        toastError();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchCompanies = async (
    targetCount: number = currentLimit
  ): Promise<CompanyDTO[]> => {
    // Fetch more companies to ensure we have enough after potential aggregation
    const fetchLimit = Math.max(targetCount, 20);
    const { data } = await api.get<CompanyDTO[]>(`/my/user/history/companies`, {
      params: {
        limit: fetchLimit,
      },
    });
    return data;
  };

  const fetchUserHistorySearches = async (
    targetCount: number = currentLimit
  ): Promise<UserHistoryDTO<UserHistoryTargetSearchCompanies>[]> => {
    // Fetch more searches to ensure we have enough after aggregation
    const fetchLimit = Math.max(targetCount * 2, 30);
    const { data } = await api.get<
      UserHistoryDTO<UserHistoryTargetSearchCompanies>[]
    >(`/my/user/history/searches`, {
      params: {
        limit: fetchLimit,
      },
    });
    return data;
  };

  const mergeAndSortHistory = (
    companies: CompanyDTO[],
    searches: UserHistoryDTO<UserHistoryTargetSearchCompanies>[],
    targetCount: number = currentLimit
  ): HistoryItem[] => {
    const companyItems: HistoryItem[] = companies.map((company) => ({
      type: "company" as const,
      id: company.id,
      title: company.name,
      lastVisited: company.lastVisited
        ? new Date(company.lastVisited)
        : new Date(),
      company,
    }));

    // Convert searches to HistoryItems without aggregation first
    const searchItems: HistoryItem[] = searches.map((search) => ({
      type: "search" as const,
      id: search.id,
      title: search.data.query,
      lastVisited: new Date(search.date),
      searchQuery: search.data.query,
      searchId: search.data.searchId,
    }));

    // Merge and sort all items by date (don't limit yet)
    const allItems = [...companyItems, ...searchItems].sort(
      (a, b) => b.lastVisited.getTime() - a.lastVisited.getTime()
    );

    // Now aggregate consecutive similar searches in the sorted list
    const aggregatedItems: HistoryItem[] = [];
    let i = 0;

    while (i < allItems.length && aggregatedItems.length < targetCount) {
      const currentItem = allItems[i];

      if (currentItem.type === "search") {
        // Look for consecutive similar searches
        const similarItems = [currentItem];
        let j = i + 1;

        while (
          j < allItems.length &&
          allItems[j].type === "search" &&
          allItems[j].searchQuery?.toLowerCase().trim() ===
            currentItem.searchQuery?.toLowerCase().trim()
        ) {
          similarItems.push(allItems[j]);
          j++;
        }

        // Create aggregated item
        aggregatedItems.push({
          ...currentItem,
          aggregatedCount:
            similarItems.length > 1 ? similarItems.length : undefined,
        });

        i = j; // Skip all the similar items we just processed
      } else {
        // Company item, just add it
        aggregatedItems.push(currentItem);
        i++;
      }
    }

    return aggregatedItems.slice(0, targetCount);
  };

  const renderHistoryIcon = (item: HistoryItem) => {
    if (item.type === "company" && item.company) {
      return <AvatarCompany className="w-8 h-8" company={item.company} />;
    }
    return (
      <div className="flex justify-center items-center w-8 h-8 rounded-full bg-muted">
        <FaHistory className="w-4 h-4 text-muted-foreground" />
      </div>
    );
  };

  const getHistoryItemLink = (item: HistoryItem) => {
    if (item.type === "company") {
      return `/company/${item.id}`;
    }
    if (item.searchId) {
      return `/prospecting/search/${item.searchId}`;
    }
    // Fallback for old history entries without searchId
    return `/prospecting/search?q=${encodeURIComponent(
      item.searchQuery || ""
    )}`;
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const newLimit = currentLimit + 10;
      const [companiesData, searchesData] = await Promise.all([
        fetchCompanies(newLimit),
        fetchUserHistorySearches(newLimit),
      ]);

      const mergedHistory = mergeAndSortHistory(
        companiesData,
        searchesData,
        newLimit
      );
      setHistoryItems(mergedHistory);
      setCurrentLimit(newLimit);

      if (companiesData.length < newLimit && searchesData.length < newLimit) {
        setCanLoadMore(false);
      }
    } catch (error) {
      toastError();
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (!isLoading && historyItems.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {isLoading && <PageSkeleton lines={8} />}
      {!isLoading && historyItems.length > 0 && (
        <>
          <div className="space-y-2">
            {historyItems.map((item) => (
              <NavLink
                key={`${item.type}-${item.id}`}
                to={getHistoryItemLink(item)}
                className="flex justify-between items-center px-2 py-2 rounded-lg transition-all cursor-pointer hover:bg-muted/50 group"
              >
                <div className="flex flex-1 gap-3 items-center min-w-0">
                  {renderHistoryIcon(item)}
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 items-center">
                      <p className="text-sm font-medium truncate transition-colors group-hover:text-primary">
                        {item.title}
                      </p>
                      {item.aggregatedCount && (
                        <Badge
                          variant="secondary"
                          className="text-xs px-1.5 py-0.5 h-5"
                        >
                          x{item.aggregatedCount}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {item.type === "company"
                        ? t("prospecting.recentHistory.company")
                        : t("prospecting.recentHistory.search")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="text-xs whitespace-nowrap transition-transform text-muted-foreground group-hover:-translate-x-2">
                    {convertDateToDistanceNow(item.lastVisited, {
                      languageOverride: currentLanguage,
                    })}
                  </div>
                  <div className="overflow-hidden w-4">
                    <FaChevronRight className="w-3 h-3 opacity-0 transition-all duration-200 translate-x-4 text-muted-foreground group-hover:translate-x-0 group-hover:opacity-100" />
                  </div>
                </div>
              </NavLink>
            ))}
          </div>
          <div className="flex justify-center mt-6">
            {canLoadMore ? (
              <Button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2Icon className="animate-spin size-4" />
                    {t("prospecting.recentSearches.loading")}
                  </>
                ) : (
                  <>
                    <PlusIcon className="size-4" />
                    {t("prospecting.recentSearches.seeMore")}
                  </>
                )}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("prospecting.recentSearches.noMore")}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
